import React , { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import AudioPlayer, { RHAP_UI }  from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import './audio-player.css';
import { setPlayerVisible, setTrackIsPlaying, selectTrack } from '../../features/audioPlayer/audioPlayerSlice';
import { cleanTrackName, 
    getNextTrackIndex, 
    getPreviousTrackIndex, 
    timeout,
    playDelay
} from '../../utils/utils';
import { useSub } from '../../hooks/usePubSub';
import { useTheme } from '@mui/material/styles';


const MusicPlayer = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const playerVisible = useSelector(state => state.audioPlayer.playerVisible)
    const trackInfo = useSelector(state => state.audioPlayer.currentTrack)
    const currentPlaylist = useSelector(state => state.audioPlayer.currentPlaylist)
    const playerRef = useRef();


    const handleToggleVisibility = () => {
        dispatch(setPlayerVisible(!playerVisible));
    }

    // auto pop the player if its 
    // not visible on song start
    const handleOnPlay = async () => {
        dispatch(setTrackIsPlaying(true));
        dispatch(setPlayerVisible(true));

        try {
            if (playerRef.current && playerRef.current.audio.current) {
                await playerRef.current.audio.current.play();
            }
        } catch (error) {
            console.error("Play request failed:", error);
        }
    }

    const handleOnPause = () => {
        dispatch(setTrackIsPlaying(false));
    }

    // handle play/pause events from external controls
    useSub('audioPlayerEvent', async (data) => {
        if (!playerRef.current || !playerRef.current.audio.current) {
            console.log('No audio assigned, exiting play/pause command');
            return;
        }
        
        const audioElement = playerRef.current.audio.current;

        switch (data.action) {
            case 'play':
                if (audioElement.paused) { // Only play if it's currently paused
                    try {
                        await audioElement.play();
                        // console.log('Audio is playing');
                    } catch (error) {
                        console.error('Play request failed:', error);
                    }
                }
                break;
            case 'pause':
                if (!audioElement.paused) { // Only pause if it's currently playing
                    setTimeout(() => {
                        audioElement.pause();
                        // console.log('Audio is paused');
                    }, 100); // Slight delay to prevent play/pause race condition
                }
                break;
            default:
                console.log('No valid action present');
                break;
        }
    });

    // get next track if a playlist exists,
    // go back to start of playlist if this
    // was the last track in the playlist
    const handleOnEnded = async () => {
        // console.log('[MusicPLayer] handleOnEnded');
        if (currentPlaylist) {
            let idx = getNextTrackIndex(trackInfo, currentPlaylist);
            dispatch(selectTrack(currentPlaylist[idx]));
            await timeout(playDelay);
            playerRef.current.audio.current.play();
        }
    }

    const handleRewind = async () => {
        // console.log('[MusicPLayer] handleRewind');
        if (currentPlaylist) {
            let idx = getPreviousTrackIndex(trackInfo,currentPlaylist);
            dispatch(selectTrack(currentPlaylist[idx]));
            await timeout(playDelay)
            playerRef.current.audio.current.play();
        }
    }

    const handleFastForward = async () => {
        // console.log('[MusicPLayer] handleFastForward');
        if (currentPlaylist) {
            let idx = getNextTrackIndex(trackInfo,currentPlaylist);
            dispatch(selectTrack(currentPlaylist[idx]));
            await timeout(playDelay)
            playerRef.current.audio.current.play();
        }
    }

    return (
        <div className={'audio-player-container ' + (playerVisible ? 'audio-player-visible' : '')}>
            <div className={'audio-player-toggle-button ' + (playerVisible ? 'audio-player-toggle-button-visible' : '')}
                onClick={() => handleToggleVisibility()}
            >
               <ExpandCircleDownIcon variant="Filled" style={{ color: theme.palette.primary.main }} />  
            </div>

            <AudioPlayer
                // autoPlay
                id='h5-audio-player'
                ref={ playerRef }
                src={`https://media.tracktribe.com/${trackInfo.track_type}/${trackInfo.url}`}
                onPlay={e => handleOnPlay()}
                onPause={e => handleOnPause()}
                onEnded={e => handleOnEnded()}
                onClickPrevious={e => handleRewind()}
                onClickNext={e => handleFastForward()}
                autoPlayAfterSrcChange={false}
                showSkipControls={true}
                showJumpControls={false}
                customControlsSection={
                    [
                    //   RHAP_UI.LOOP,
                      <div className='audio-player-track-info-block'>
                        {trackInfo && 
                            <>
                                <img className='audio-player-track-preview-icon' src={`https://media.tracktribe.com/images/track_images/${trackInfo.image}`} alt='' />
                                <div className='audio-player-track-info-block-details'>
                                    <div className='audio-player-track-label'>{cleanTrackName(trackInfo.title)}</div>
                                    <div className='audio-player-artist-label'>{trackInfo.artist?.artist_name || trackInfo.artist_name}</div>
                                </div>
                                
                            </>
                        }
                        
                      </div>,
                      RHAP_UI.MAIN_CONTROLS,
                      RHAP_UI.VOLUME_CONTROLS,
                    ]
                  }
                //   customIcons={{
                //     play: PlayIcon
                //   }}
            />
        </div>
    )
};

export default MusicPlayer;